import React, { useCallback } from 'react';
import { defineMessages, FormattedNumber, useIntl, type MessageValue } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import {
  BodyText,
  CloudinaryImage,
  type CloudinaryTransformConfig,
  QuantityStepperV2,
  theme,
  useQuantityStepperV2,
} from '@sg/garnish';
import type { DeliveryOrderDetailInput } from '@sg/graphql-schema';

import { telemetryProductFromInput, useTelemetry } from '@order/Telemetry';

import { useAddLineItemToCart } from '../../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Upsell product option component.
 *
 * @note This component is memoized because the parent re-renders due to tracking open stepper state.
 */
export const UpsellProductOption = React.memo((props: UpsellProductOptionProps) => {
  const { product, size, withBorder, onAddToBag, updateOpenSteppers, position, deliveryDetails } =
    props;
  const { name, imageUrl, cost, channelCost } = product;

  const isLarge = size === 'lg';

  const { track } = useTelemetry();
  const { formatMessage } = useIntl();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onSuccessfulAddToBag = useCallback<OnSuccess>(
    (payload) => {
      const { quantity, product: addedProduct } = payload;
      const telemetryProduct = telemetryProductFromInput(addedProduct);

      track('upsell_modal.product_added_to_bag', { quantity, product: telemetryProduct, position }); // prettier-ignore
      onAddToBag();
    },
    [onAddToBag, track, position],
  );

  const { addLineItemToCart, fetching: isAddingToCart } = useAddLineItemToCart({
    onSuccess: onSuccessfulAddToBag,
  });

  const handleAddUpsell = useCallback(
    (quantity: number) => {
      void addLineItemToCart({ quantity, product, deliveryDetails });
    },
    [addLineItemToCart, product, deliveryDetails],
  );

  const onShowStepper = useCallback(() => {
    updateOpenSteppers(product.id, true);
  }, [product.id, updateOpenSteppers]);

  const onHideStepper = useCallback(() => {
    updateOpenSteppers(product.id, false);
  }, [product.id, updateOpenSteppers]);

  const { quantity, isStepperShown, increaseQuantity, decreaseQuantity, showStepper } =
    useQuantityStepperV2({
      onChange: handleAddUpsell,
      shouldResetOnTimeout: true,
      onShowStepper,
      onHideStepper,
    });

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyle = [styles.container, withBorder ? styles.containerWithBorder : undefined];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyle}>
      <CloudinaryImage
        baseUrl={imageUrl}
        config={isLarge ? LARGE_CLOUDINARY_IMG_CONFIG : CLOUDINARY_IMG_CONFIG}
        contentFit="contain"
        style={isLarge ? styles.largeImage : styles.image}
        aria-label={name}
      />

      <View style={styles.textContainer}>
        <BodyText bold sizeMatch={['18']} numberOfLines={2}>
          {name}
        </BodyText>

        <BodyText sizeMatch={['16']}>
          <FormattedNumber value={(channelCost ?? cost) / 100} style="currency" currency="USD" />
        </BodyText>
      </View>

      <View style={styles.stepperContainer}>
        <QuantityStepperV2.Container>
          <QuantityStepperV2.ToggleButton
            accessibilityLabel={formatMessage(messages.addToBagA11yLabel, { product_name: name })} // prettier-ignore
            isLoading={isAddingToCart}
            onPress={showStepper}
            size="md"
          />
          <QuantityStepperV2.FloatingContainer isShown={isStepperShown} size="lg">
            <QuantityStepperV2.DecreaseButton
              onPress={decreaseQuantity}
              accessibilityLabel={formatMessage(messages.decreaseQuantity, { product_name: name })} // prettier-ignore
              size="md"
            />

            {isAddingToCart ? null : <QuantityStepperV2.Quantity quantity={quantity} />}

            <QuantityStepperV2.IncreaseButton
              onPress={increaseQuantity}
              accessibilityLabel={formatMessage(messages.increaseQuantity, { product_name: name })} // prettier-ignore
              size="md"
            />
          </QuantityStepperV2.FloatingContainer>
        </QuantityStepperV2.Container>
      </View>
    </View>
  );
});

// ─── Constants ──────────────────────────────────────────────────────────────

const CARD_IMG_SIZE = {
  width: 100,
  height: 100,
};

const LARGE_CARD_IMG_SIZE = {
  width: 124,
  height: 124,
};

const CLOUDINARY_IMG_CONFIG: CloudinaryTransformConfig = [
  { crop: 'crop', width: 0.4, height: 0.56 },
  { width: CARD_IMG_SIZE.width },
];

const LARGE_CLOUDINARY_IMG_CONFIG: CloudinaryTransformConfig = [
  { crop: 'crop', width: 0.4, height: 0.56 },
  { width: LARGE_CARD_IMG_SIZE.width },
];

// ─────────────────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly addToBagA11yLabel: { readonly product_name: MessageValue };
  readonly increaseQuantity: { readonly product_name: MessageValue };
  readonly decreaseQuantity: { readonly product_name: MessageValue };
}>({
  addToBagA11yLabel: {
    defaultMessage: 'Add {product_name} to bag',
    description: 'Upsell modal | Product | Add to bag',
  },
  increaseQuantity: {
    defaultMessage: 'Increase quantity of "{product_name}"',
    description: 'Upsell | Product | Increase quantity of',
  },
  decreaseQuantity: {
    defaultMessage: 'Decrease quantity of "{product_name}"',
    description: 'Upsell | Product | Decrease quantity of',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: 80,
    paddingBottom: theme.spacing['4'],
    paddingRight: theme.spacing['4'],
  },
  containerWithBorder: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.OPACITY.KALE.LIGHTEST,
  },
  image: {
    width: CARD_IMG_SIZE.width,
    height: CARD_IMG_SIZE.height,
  },
  largeImage: {
    width: LARGE_CARD_IMG_SIZE.width,
    height: LARGE_CARD_IMG_SIZE.height,
  },
  textContainer: {
    flex: 1,
    paddingRight: theme.spacing['5'],
    paddingLeft: theme.spacing['2'],
    alignSelf: 'center',
    gap: theme.spacing['2'],
  },
  stepperContainer: {
    paddingTop: theme.spacing['2'],
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type UpsellProductOptionProps = {
  product: ProductUpsell;
  size: 'md' | 'lg';
  withBorder: boolean;
  onAddToBag: () => void;
  updateOpenSteppers: (productId: string, isStepperShown: boolean) => void;
  position: number;
  deliveryDetails?: DeliveryOrderDetailInput;
};

type ProductUpsell = {
  id: string;
  name: string;
  cost: number;
  channelCost?: number;
  imageUrl: string;
};

type AddLineItemToCartParams = NonNullable<Parameters<typeof useAddLineItemToCart>[0]>;

type OnSuccess = NonNullable<AddLineItemToCartParams['onSuccess']>;
